import { AxiosError } from 'axios';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { LoadingScreen } from 'src/components';
import * as fromRoot from 'src/state/rootReducer';
import { fetchUserData } from 'src/state/UserData';
import Onboarding from '../Onboarding';
import {
  Background,
  Cookie,
  ErrorCard,
  Footer,
  Header,
  ProcessingCard,
  SessionTimeout,
} from './components';
import TechnicalError from './TechnicalError';
import { isForbidden } from 'src/utils/Errors';
import { Config } from 'src/state/Config';
import { useUrlParameterCheck } from 'src/utils/useUrlParameterCheck';
import { LayoutWrapper } from 'src/pages/Layout/Layout.styles';

interface StateProps {
  customerCreationError: AxiosError | null;
  isUserLoaded: boolean;
  isCreatingCustomer: boolean;
  isCustomerCreated: boolean;
  isLoadingUserData: boolean;
  shouldOnboardCustomer: boolean;
  userDataError: AxiosError | null;
  userId?: string;
}

interface DispatchProps {
  fetchUser: typeof fetchUserData.request;
}

export type Props = StateProps &
  DispatchProps &
  RouteComponentProps<any> &
  WithTranslation;

const Layout: React.FC<Props> = ({
  children,
  customerCreationError,
  userDataError,
  userId,
  isUserLoaded,
  isLoadingUserData,
  isCreatingCustomer,
  shouldOnboardCustomer,
  isCustomerCreated,
  t,
  fetchUser,
}) => {
  const isFromMvpApp = useUrlParameterCheck('/app');
  const isDataLoadingFromMvpApp =
    isLoadingUserData && !isCustomerCreated && isFromMvpApp;
  const isLoadingDataCustomerCreated = isLoadingUserData && !isCustomerCreated;
  const isNoData = !userId && !userDataError && !isCustomerCreated;

  useEffect(() => {
    if (!userId) {
      fetchUser();
    }
  }, [userId, fetchUser]);

  if (isDataLoadingFromMvpApp || isLoadingDataCustomerCreated || isNoData) {
    return (
      <Background>
        <LoadingScreen text={t('Loading')} data-testid="app-loading-screen" />
      </Background>
    );
  }

  const renderContent = () => {
    if (isCreatingCustomer || (isLoadingUserData && isCustomerCreated)) {
      return <ProcessingCard />;
    }
    if (customerCreationError) {
      return <ErrorCard />;
    }
    if (userDataError && !shouldOnboardCustomer) {
      return (
        <TechnicalError
          title={t(getErrorTitle(userDataError))}
          info={t(getErrorInformation(userDataError))}
        />
      );
    }
    if (shouldOnboardCustomer) {
      return <Onboarding />;
    }
    return children;
  };

  return (
    <>
      <LayoutWrapper>
        <Header showNavigation={isUserLoaded} data-testid="header" />
        <SessionTimeout />
        {renderContent()}
        {isFromMvpApp ? null : (
          <Footer
            hasTwoPhoneNumbers={hasTwoPhoneNumbers()}
            userId={userId}
            data-testid="footer"
          />
        )}
      </LayoutWrapper>
      <Cookie />
    </>
  );
};

export const getErrorTitle = (error: AxiosError) =>
  error
    ? isForbidden(error)
      ? 'UnauthorisedAccess'
      : 'InternalServerError'
    : '';

export const hasTwoPhoneNumbers = () =>
  Config.isFinlandSite() ||
  Config.isSwedishSite() ||
  Config.isNorwaySite() ||
  Config.isRealkreditDenmark();

export const getErrorInformation = (error: AxiosError) =>
  error && !isForbidden(error) ? 'TechnicalError' : '';

export default withRouter(
  connect(
    state => ({
      customerCreationError: fromRoot.getCustomerCreationError(state),
      isCreatingCustomer: fromRoot.isCreatingCustomer(state),
      isCustomerCreated: fromRoot.isCustomerCreated(state),
      isLoadingUserData: fromRoot.getIsUserDataLoading(state),
      isUserLoaded: fromRoot.getIsUserLoaded(state),
      userDataError: fromRoot.getUserDataError(state),
      shouldOnboardCustomer: fromRoot.getShouldOnboardCustomer(state),
      userId: fromRoot.getUserId(state),
    }),
    {
      fetchUser: fetchUserData.request,
    }
  )(withTranslation()(Layout))
);
